import { useCallback, useMemo, useState, type Dispatch } from 'react';
import { textCommitAction, type TextDraft } from './markupDrafts';
import type { Size } from './markupGeometry';
import { canRedo, canUndo, hasMarks, type MarkupAction, type MarkupState } from './markupHistory';
import { restyleShape, sizeOfShape, type StyleChange } from './markupRestyle';
import type { MarkupShape, MarkupTool, TextShape } from './markupShapes';
import type { MarkupCommand } from './markupShortcuts';
import { DEFAULT_MARKUP_COLOR, DEFAULT_MARKUP_SIZE, type MarkupSize } from './markupStyle';
import { useMarkupShortcuts } from './useMarkupShortcuts';
import { useTextDraft, type TextDraftControls } from './useTextDraft';

interface UseMarkupEditorArgs {
    markup: MarkupState;
    dispatch: Dispatch<MarkupAction>;
    image: Size | null;
    shortcutsEnabled: boolean;
}

export interface MarkupEditor {
    tool: MarkupTool;
    setTool: (tool: MarkupTool) => void;
    color: string;
    changeColor: (color: string) => void;
    size: MarkupSize;
    changeSize: (size: MarkupSize) => void;
    selected: MarkupShape | null;
    select: (shape: MarkupShape | null) => void;
    text: TextDraftControls;
    history: { canUndo: boolean; canRedo: boolean; canClear: boolean };
    undo: () => void;
    redo: () => void;
    clear: () => void;
}

const INITIAL_TOOL: MarkupTool = 'pen';

function findLabel(shapes: readonly MarkupShape[], id: string): TextShape | null {
    const shape = shapes.find((candidate) => candidate.id === id);
    return shape?.kind === 'text' ? shape : null;
}

export function useMarkupEditor({
    markup,
    dispatch,
    image,
    shortcutsEnabled
}: UseMarkupEditorArgs): MarkupEditor {
    const [tool, setToolState] = useState<MarkupTool>(INITIAL_TOOL);
    const [color, setColor] = useState(DEFAULT_MARKUP_COLOR.value);
    const [size, setSize] = useState<MarkupSize>(DEFAULT_MARKUP_SIZE);
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const selected = useMemo(
        () => markup.shapes.find((shape) => shape.id === selectedId) ?? null,
        [markup.shapes, selectedId]
    );

    const commitText = useCallback(
        (draft: TextDraft) => {
            const original = draft.editing ? findLabel(markup.shapes, draft.id) : null;
            const action = textCommitAction(draft, original);
            if (action) dispatch(action);
        },
        [markup.shapes, dispatch]
    );
    const text = useTextDraft(commitText);

    // Selecting a mark loads its colour and size, so the bar shows what it is drawn with.
    const select = useCallback(
        (shape: MarkupShape | null) => {
            setSelectedId(shape?.id ?? null);
            if (!shape) return;
            setColor(shape.color);
            const shapeSize = image ? sizeOfShape(shape, image) : null;
            if (shapeSize) setSize(shapeSize);
        },
        [image]
    );

    const setTool = useCallback((next: MarkupTool) => {
        setToolState(next);
        if (next !== 'select') setSelectedId(null);
    }, []);

    const restyleSelected = useCallback(
        (change: StyleChange) => {
            if (selected && image)
                dispatch({ type: 'update', shape: restyleShape(selected, change, image) });
        },
        [selected, image, dispatch]
    );

    const changeColor = useCallback(
        (next: string) => {
            setColor(next);
            restyleSelected({ color: next });
        },
        [restyleSelected]
    );

    const changeSize = useCallback(
        (next: MarkupSize) => {
            setSize(next);
            restyleSelected({ size: next });
        },
        [restyleSelected]
    );

    const undo = useCallback(() => dispatch({ type: 'undo' }), [dispatch]);
    const redo = useCallback(() => dispatch({ type: 'redo' }), [dispatch]);
    const clear = useCallback(() => {
        setSelectedId(null);
        dispatch({ type: 'clear' });
    }, [dispatch]);

    const runCommand = useCallback(
        (command: MarkupCommand) => {
            if (command.type === 'undo') undo();
            else if (command.type === 'redo') redo();
            else if (command.type === 'tool') setTool(command.tool);
            else if (command.type === 'deselect') setSelectedId(null);
            else if (selected) {
                setSelectedId(null);
                dispatch({ type: 'remove', id: selected.id });
            }
        },
        [undo, redo, setTool, selected, dispatch]
    );

    useMarkupShortcuts({
        enabled: shortcutsEnabled,
        hasSelection: selected != null,
        onCommand: runCommand
    });

    const history = {
        canUndo: canUndo(markup),
        canRedo: canRedo(markup),
        canClear: hasMarks(markup)
    };

    return {
        tool,
        setTool,
        color,
        changeColor,
        size,
        changeSize,
        selected,
        select,
        text,
        history,
        undo,
        redo,
        clear
    };
}
